import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Button, Dialog, Grid, TextField } from "@mui/material";

import { Props, State } from "./interfaces";
import { ActionButton, DialogContent } from "./styles";
import DialogTitle from "../../../../DialogTitle/DialogTitle";
import DialogActions from "../../../../DialogActions/DialogActions";
import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { CurrenciesApiService } from "../../../../../services/currencies/currencies-api.service";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import { TransactionToLoanAttributionRef } from "../../../../../models/transaction-to-loan-attribution";
import { LegalEntityLoansApiService } from "../../../../../services/legal-entity-loans/legal-entity-loans-api.service";
import { PropsFilter } from "../../../../../models/api/props-filter";
import { isEqual } from "lodash";
import ExchangeRateBookingsSuggestions from "../../ExchangeRateBookingsSuggestions/ExchangeRateBookingsSuggestions";
import { ExchangeRatesApiService } from "../../../../../services/exchange-rates";
import { ExchangeRatesFilter } from "../../../../../models/exchange-rates";
import ConfirmationChangesDialog from "../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import { TextFieldTypeAmount } from "../../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";

const AddEditLoansRow = ({ handleChangeLoan, legalEntity, item, propsNotIds, currency, date }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_two.loan"; // tRP - translation root path

  const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);

  const notIds = useMemo(() => {
    return item ? propsNotIds.filter((el) => el !== (item?.loan?.id as string)) : propsNotIds;
  }, [item, propsNotIds]);

  const DEFAULT_ITEM: Partial<TransactionToLoanAttributionRef> = useMemo(() => {
    return {
      legalEntity: item?.legalEntity ?? legalEntity ?? null,
      currency: item?.currency ?? currency ?? null,
      bookedCurrency: item?.bookedCurrency ?? currency ?? null,
      loan: item?.loan ?? null,
      transaction: item?.transaction ?? null,
      amount: item?.amount ?? 0,
      exchangeRate: item?.exchangeRate ?? 1,
      bookedAmount: item?.bookedAmount ?? 0,
    };
  }, [legalEntity, item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loan: item ?? (DEFAULT_ITEM as TransactionToLoanAttributionRef),
      exchangeRateSuggestions: [],
      isCCDOpen: false,
      areFieldsValidated: !!item,
    };
  }, [DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeLoanField = useCallback(
    (
      field: keyof TransactionToLoanAttributionRef,
      value: TransactionToLoanAttributionRef[keyof TransactionToLoanAttributionRef]
    ) => {
      if (field === "bookedAmount") {
        const exchangeRate = !value || !state.loan.amount ? "0" : ((value as number) / state.loan.amount).toFixed(4);

        setState((state) => ({
          ...state,
          loan: { ...state.loan, [field]: value as number, exchangeRate: parseFloat(exchangeRate) },
        }));
      } else if (field === "amount" || field === "exchangeRate") {
        const bookedAmount = !value
          ? "0"
          : field === "amount"
          ? ((value as number) * state.loan.exchangeRate).toFixed(2)
          : ((value as number) * state.loan.amount).toFixed(2);

        setState((state) => ({
          ...state,
          loan: { ...state.loan, [field]: value, bookedAmount: parseFloat(bookedAmount) },
        }));
      } else {
        setState((state) => ({
          ...state,
          loan: { ...state.loan, [field]: value },
        }));
      }
    },
    [state.loan]
  );

  const handleConfirm = useCallback(async () => {
    handleChangeLoan({
      ...state.loan,
      id: item ? item.id : uuidv4(),
      companyAccount: item ? item.companyAccount : currCompanyAccount,
    });

    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE, item, handleChangeLoan, state.loan, currCompanyAccount]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.loan, INITIAL_STATE.loan)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  useEffect(() => {
    !item &&
      setState((state) => ({
        ...state,
        loan: {
          ...state.loan,
          legalEntity: legalEntity ?? null,
          currency: currency ?? null,
          bookedCurrency: currency ?? null,
        },
      }));
  }, [item, legalEntity, currency]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        loan: item,
      }));

    return () => {
      if (!item) setState({ ...INITIAL_STATE });
    };
  }, [item, INITIAL_STATE]);

  const loadExchangeRates = useCallback(
    async (signal?: AbortSignal) => {
      const apiService = new ExchangeRatesApiService();
      apiService.useLoadItemsPath("/GetSuggestedExchangeRate");

      try {
        const response = await apiService.loadItems(
          {
            sourceId: state.loan?.currency?.id,
            destinationId: state.loan?.bookedCurrency?.id,
            year: new Date(date).getFullYear(),
            month: new Date(date).getMonth() + 1,
            legalEntityId: state.loan?.legalEntity?.id,
          } as ExchangeRatesFilter,
          undefined,
          undefined,
          undefined,
          undefined,
          60,
          signal
        );

        if (response.error || signal?.aborted || !signal) {
          setState((state) => ({
            ...state,
            error: response.error,
          }));
        } else {
          setState((state) => ({
            ...state,
            loan: {
              ...state.loan,
              exchangeRate: response.items[0]?.rate ?? 1,
              bookedAmount:
                response.items[0]?.rate && !!state.loan.amount
                  ? parseFloat((response.items[0].rate * state.loan.amount).toFixed(2))
                  : state.loan.bookedAmount,
            },
            exchangeRateSuggestions: response.items,
          }));
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          error: error as string,
        }));
      }
    },
    [date, state.loan?.bookedCurrency, state.loan?.currency, state.loan?.legalEntity]
  );

  useEffect(() => {
    const abortController = new AbortController();

    state.isDialogOpen &&
      !!state.loan?.legalEntity &&
      !!date &&
      !!state.loan?.bookedCurrency &&
      !!state.loan?.currency &&
      loadExchangeRates(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [date, state.loan?.bookedCurrency, state.loan?.currency, state.loan?.legalEntity, state.isDialogOpen]);

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.loan.loan &&
      !!state.loan.amount &&
      !!state.loan.bookedAmount &&
      !!state.loan.currency &&
      !!state.loan.bookedCurrency &&
      !!state.loan.exchangeRate
    );
  }, [state.loan]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.loan, areFieldsValidated]);

  return (
    <>
      {item ? (
        <ActionButton
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          <FontAwesomeIcon icon={faPencil} />
        </ActionButton>
      ) : (
        <Button
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          <FontAwesomeIcon
            icon={faPlusCircle}
            style={{ width: "25px", height: "25px" }}
          />
        </Button>
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
      >
        <DialogTitle
          title={item ? t(`${tRP}.edit_title`) : t(`${tRP}.title`)}
          handleClose={resetAndCloseDialog}
        />
        <DialogContent>
          <AutocompleteDropdown
            labelKey={`${t(`${tRP}.fields.loan`)}`}
            propsItem={state.loan}
            field="loan"
            serviceApi={new LegalEntityLoansApiService()}
            handleChange={handleChangeLoanField}
            propsFilter={
              {
                notIds: notIds,
                legalEntityIds: legalEntity?.id ? [legalEntity?.id] : [],
              } as PropsFilter
            }
            required
          />

          <Grid
            sx={{
              width: "45%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",

              ".MuiAutocomplete-root, & > .MuiFormControl-root": {
                width: "100%%",
              },
            }}
          >
            <TextFieldTypeAmount
              field="amount"
              labelKey={`${t(`${tRP}.fields.amount`)}`}
              handleChange={(_, number) => handleChangeLoanField("amount", number)}
              amountValue={state.loan.amount ?? 0}
              sxProps={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              endAdornment={
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.currency`)}`}
                  propsItem={state.loan}
                  field={"currency"}
                  handleChange={handleChangeLoanField}
                  serviceApi={new CurrenciesApiService()}
                  noLabel={true}
                  inputStyle={{
                    padding: "1px 46px 1px 1px",
                    width: "100px",
                  }}
                  fieldProp={"code"}
                />
              }
            />

            {/* <TextField
              label={`${t(`${tRP}.fields.amount`)}`}
              onChange={(e) => handleChangeLoanField("amount", e.target.value)}
              type="number"
              value={state.loan.amount}
              sx={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <AutocompleteDropdown
                    labelKey={`${t(`${tRP}.fields.currency`)}`}
                    propsItem={state.loan}
                    field={"currency"}
                    handleChange={handleChangeLoanField}
                    serviceApi={new CurrenciesApiService()}
                    noLabel={true}
                    inputStyle={{
                      padding: "1px 46px 1px 1px",
                      width: "100px",
                    }}
                    fieldProp={"code"}
                    disabled
                  />
                ),
              }}
            /> */}

            <TextField
              label={`${t(`${tRP}.fields.exchange_rate`)}`}
              type="number"
              onChange={(event) => {
                handleChangeLoanField("exchangeRate", event.currentTarget.value);
              }}
              value={state.loan.exchangeRate}
              disabled={isEqual(state.loan?.currency?.id, state.loan?.bookedCurrency?.id)}
              InputProps={{
                endAdornment: (
                  <ExchangeRateBookingsSuggestions exchangeRateSuggestions={state.exchangeRateSuggestions} />
                ),
              }}
            />

            <TextFieldTypeAmount
              field="bookedAmount"
              labelKey={`${t(`${tRP}.fields.converted_amount`)}`}
              handleChange={(_, number) => handleChangeLoanField("bookedAmount", number)}
              amountValue={state.loan.bookedAmount ?? 0}
              sxProps={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              endAdornment={
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.currency`)}`}
                  propsItem={state.loan}
                  field={"bookedCurrency"}
                  handleChange={handleChangeLoanField}
                  serviceApi={new CurrenciesApiService()}
                  noLabel={true}
                  inputStyle={{
                    padding: "1px 46px 1px 1px",
                    width: "100px",
                  }}
                  fieldProp={"code"}
                />
              }
            />
            {/* <TextField
              label={`${t(`${tRP}.fields.converted_amount`)}`}
              type="number"
              value={state.loan.bookedAmount}
              onChange={(e) => handleChangeLoanField("bookedAmount", e.target.value)}
              sx={{
                "& .MuiCircularProgress-root": {
                  position: "absolute",
                  marginLeft: "69px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <>
                    <AutocompleteDropdown
                      labelKey={`${t(`${tRP}.fields.currency`)}`}
                      propsItem={state.loan}
                      field={"bookedCurrency"}
                      handleChange={handleChangeLoanField}
                      serviceApi={new CurrenciesApiService()}
                      noLabel={true}
                      inputStyle={{
                        padding: "1px 46px 1px 1px",
                        width: "100px",
                      }}
                      fieldProp={"code"}
                    />
                  </>
                ),
              }}
            /> */}
          </Grid>
        </DialogContent>
        <DialogActions
          handleCancel={resetAndCloseDialog}
          handleSave={handleConfirm}
          customSaveName={`${t("buttons.confirm")}`}
          disabledSaveButton={!areFieldsValidated()}
        />
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditLoansRow;
